import { useQuery } from "@tanstack/react-query";

import supabase from "../lib/supabase";
import type { UserRole } from "../types/domain";

const KNOWN_ROLES: UserRole[] = [
  "super_admin",
  "tenant_admin",
  "supervisor",
  "operator",
  "finance",
  "legal",
  "read_only",
  "creditor_portal_user",
  "debtor_portal_user",
  "lawyer_portal_user",
  "platform_support",
];

/** UI/org labels that map to canonical `tenant_admin` in DB migrations. */
const ROLE_ALIASES_TO_CANONICAL: Record<string, UserRole> = {
  org_admin: "tenant_admin",
  organization_admin: "tenant_admin",
};

function parseUserRole(value: string | null | undefined): UserRole | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (KNOWN_ROLES.includes(trimmed as UserRole)) {
    return trimmed as UserRole;
  }
  const lower = trimmed.toLowerCase();
  if (KNOWN_ROLES.includes(lower as UserRole)) {
    return lower as UserRole;
  }
  const mapped = ROLE_ALIASES_TO_CANONICAL[lower];
  return mapped ?? null;
}

/**
 * Loads `public.users.role` for the signed-in auth user (`users_self` RLS).
 */
export function useCurrentUserRole(userId: string | null | undefined): {
  role: UserRole | null;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ["current-user-role", userId],
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const result = await supabase.from("users").select("role").eq("id", userId ?? "").maybeSingle();
      if (result.error) {
        throw new Error(result.error.message);
      }
      return parseUserRole(result.data?.role ?? null);
    },
  });

  return {
    role: query.data ?? null,
    // Wait until we have a settled fetch result: `data === undefined` means "not completed yet"
    // (success with `null` role is valid and must not keep spinning forever).
    isLoading: Boolean(userId) && query.data === undefined && !query.isError,
    isError: query.isError,
  };
}
